import React, { useMemo } from 'react';
import { Timeline, Tag, Typography, Empty, Tooltip } from 'antd';
import { ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import type { Task, TaskStatus } from '../../types/triage';

dayjs.extend(relativeTime);

const STATUS_TINT: Record<TaskStatus, { color: string; dot: React.ReactNode }> = {
  pending:        { color: 'blue',   dot: <ClockCircleOutlined style={{ fontSize: 14, color: '#1677ff' }} /> },
  'in-progress':  { color: 'orange', dot: <LoadingOutlined style={{ fontSize: 14, color: '#fa8c16' }} /> },
  accepted:       { color: 'cyan',   dot: <ClockCircleOutlined style={{ fontSize: 14, color: '#13c2c2' }} /> },
  done:           { color: 'green',  dot: <CheckCircleOutlined style={{ fontSize: 14, color: '#52c41a' }} /> },
  'auto-resolved':{ color: 'purple', dot: <CheckCircleOutlined style={{ fontSize: 14, color: '#722ed1' }} /> },
  canceled:       { color: 'default',dot: <CloseCircleOutlined style={{ fontSize: 14, color: '#8c8c8c' }} /> },
};

interface TaskTimelineProps {
  tasks: Task[];
}

// Visualizes each task as a timeline item ordered by created_at desc.
// Shows status transitions implicitly via the created → completed date range
// and a status tag; richer per-status timestamps would require a task_events
// table which doesn't exist yet.
const TaskTimeline: React.FC<TaskTimelineProps> = ({ tasks }) => {
  const sorted = useMemo(
    () => [...tasks].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [tasks],
  );

  if (sorted.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No tasks yet" style={{ padding: '12px 0' }} />;
  }

  return (
    <Timeline
      mode="left"
      items={sorted.map((task) => {
        const tint = STATUS_TINT[task.status];
        const created = dayjs(task.created_at);
        const completed = task.completed_at ? dayjs(task.completed_at) : null;
        const isOverdue = task.due_date
          && !completed
          && dayjs().isAfter(dayjs(task.due_date))
          && !['done', 'auto-resolved', 'canceled'].includes(task.status);
        return {
          dot: tint.dot,
          children: (
            <div style={{ paddingBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 2 }}>
                <Typography.Text strong style={{ fontSize: 12 }}>{task.title}</Typography.Text>
                <Tag color={tint.color} style={{ margin: 0, fontSize: 10 }}>{task.status}</Tag>
                {isOverdue && <Tag color="red" style={{ margin: 0, fontSize: 10 }}>OVERDUE</Tag>}
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                <Tooltip title={created.format('YYYY-MM-DD HH:mm')}>
                  <span>created {created.fromNow()}</span>
                </Tooltip>
                {task.executor && <> · assigned to {task.executor}</>}
                {task.due_date && (
                  <Tooltip title={dayjs(task.due_date).format('YYYY-MM-DD')}>
                    <span> · due {dayjs(task.due_date).fromNow()}</span>
                  </Tooltip>
                )}
                {completed && (
                  <Tooltip title={completed.format('YYYY-MM-DD HH:mm')}>
                    <span> · completed {completed.fromNow()}</span>
                  </Tooltip>
                )}
              </Typography.Text>
            </div>
          ),
        };
      })}
    />
  );
};

export default TaskTimeline;
